/**
 * The policy document, in the shape a form can bind to.
 *
 * This is a deliberate second implementation of the server's `PolicyDocument`, and the reason is that
 * the guided editor has to redraw the JSON on every keystroke. Asking the server what the document
 * looks like after each one would make the form lag behind the cursor, and a form that lags is a form
 * people abandon for the JSON box. Validation is still the server's — this module never decides
 * whether a document is acceptable, only what it says.
 *
 * The two properties everything else rests on:
 *
 *   parse(serialize(document)) === document, for every document the server can produce
 *   serialize() preserves every key it does not manage
 *
 * The second matters more than it looks. The agent's own guided editor rebuilds `parameters` from its
 * form fields and silently drops the `enforcement` block, which is the part that actually stops a
 * prompt being sent. Here `parameters` is edited in place, so a key this console has never heard of
 * survives being opened, edited, and saved.
 */

export type TriggerKind = 'startup' | 'interval' | 'daily' | 'event'

export interface PolicyTrigger {
  kind: TriggerKind
  seconds?: number
  localTime?: string
  timeZoneId?: string
  name?: string
}

export interface PolicyRetry {
  maximumRetries: number
  initialSeconds: number
  maximumSeconds: number
}

export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }
export type JsonObject = { [key: string]: JsonValue }

export interface PolicyDraft {
  id: string | null
  revision: number
  name: string
  description: string
  capability: string
  parameters: JsonObject
  trigger: PolicyTrigger
  condition: string
  retry: PolicyRetry | null
  publish: boolean
  /**
   * Narrows publication to the results that satisfy it, evaluated on the device against the capability
   * result. Empty means publish everything the policy produces.
   */
  publishCondition: string
  /** Every top-level key this console does not manage, re-emitted verbatim. */
  extras: JsonObject
}

export interface ContentRuleDraft {
  kind: 'content'
  name: string
  matchAny: string[]
  severity: string
  caseSensitive: boolean
  wholeWord: boolean
}

export interface SignalRuleDraft {
  kind: 'signal'
  name: string
  matchAny: string[]
  caseSensitive: boolean
}

export type DetectionRuleDraft = ContentRuleDraft | SignalRuleDraft

const MANAGED = new Set([
  'id',
  'revision',
  'name',
  'description',
  'capability',
  'parameters',
  'trigger',
  'condition',
  'retry',
  'publish',
  'publishCondition'
])

export const DEFAULT_RETRY: PolicyRetry = { maximumRetries: 3, initialSeconds: 5, maximumSeconds: 300 }

export function emptyDraft(): PolicyDraft {
  return {
    id: null,
    revision: 1,
    name: '',
    description: '',
    capability: 'log',
    parameters: { message: 'New policy' },
    trigger: { kind: 'startup' },
    condition: '',
    retry: null,
    publish: false,
    publishCondition: '',
    extras: {}
  }
}

export class PolicyParseError extends Error {}

export function parseDraft(json: string): PolicyDraft {
  let root: unknown
  try {
    root = JSON.parse(json)
  } catch (error) {
    throw new PolicyParseError(error instanceof Error ? error.message : 'The document is not valid JSON.')
  }

  if (root === null || typeof root !== 'object' || Array.isArray(root))
    throw new PolicyParseError('A policy document must be a JSON object.')

  const source = root as Record<string, unknown>
  const extras: JsonObject = {}
  for (const [key, value] of Object.entries(source)) if (!MANAGED.has(key)) extras[key] = value as JsonValue

  return {
    id: typeof source.id === 'string' ? source.id : null,
    revision: typeof source.revision === 'number' ? source.revision : 1,
    name: typeof source.name === 'string' ? source.name : '',
    description: typeof source.description === 'string' ? source.description : '',
    capability: typeof source.capability === 'string' ? source.capability : '',
    parameters: isObject(source.parameters) ? (source.parameters as JsonObject) : {},
    trigger: parseTrigger(source.trigger),
    condition: typeof source.condition === 'string' ? source.condition : '',
    retry: parseRetry(source.retry),
    publish: source.publish === true,
    publishCondition: typeof source.publishCondition === 'string' ? source.publishCondition : '',
    extras
  }
}

/**
 * Serializes in the same property order the server's `PolicyDocument.ToJson` uses.
 *
 * Order is not meaningful to a JSON reader, but the publication hash is taken over these bytes, so
 * matching it is what lets an operator compare what the editor shows with what was published without
 * being told they differ when they do not.
 */
export function serializeDraft(draft: PolicyDraft, indented = true): string {
  const root: JsonObject = {}
  if (draft.id) root.id = draft.id
  root.revision = draft.revision
  if (draft.name.trim()) root.name = draft.name
  if (draft.description.trim()) root.description = draft.description
  root.capability = draft.capability
  root.parameters = draft.parameters
  root.trigger = triggerToJson(draft.trigger)
  // A condition only means something on an event trigger, and one left behind by a trigger change is
  // a policy that silently never runs.
  if (draft.trigger.kind === 'event' && draft.condition.trim()) root.condition = draft.condition
  if (draft.retry) root.retry = { ...draft.retry }
  root.publish = draft.publish
  // Only meaningful alongside publish, and the server refuses the pair the other way round: a
  // condition on a policy that never publishes reads as a control that is in force when it is not.
  if (draft.publish && draft.publishCondition.trim()) root.publishCondition = draft.publishCondition.trim()
  for (const [key, value] of Object.entries(draft.extras)) root[key] = value

  return JSON.stringify(root, null, indented ? 2 : 0)
}

function parseTrigger(value: unknown): PolicyTrigger {
  if (!isObject(value)) return { kind: 'startup' }
  const trigger = value as Record<string, unknown>
  switch (String(trigger.kind ?? 'startup').toLowerCase()) {
    case 'interval':
      return { kind: 'interval', seconds: typeof trigger.seconds === 'number' ? trigger.seconds : 300 }
    case 'daily':
      return {
        kind: 'daily',
        localTime: typeof trigger.localTime === 'string' ? trigger.localTime : '09:00',
        timeZoneId: typeof trigger.timeZoneId === 'string' ? trigger.timeZoneId : undefined
      }
    case 'event':
      return { kind: 'event', name: typeof trigger.name === 'string' ? trigger.name : '' }
    default:
      return { kind: 'startup' }
  }
}

function triggerToJson(trigger: PolicyTrigger): JsonObject {
  switch (trigger.kind) {
    case 'interval':
      return { kind: 'interval', seconds: trigger.seconds ?? 300 }
    case 'daily':
      return trigger.timeZoneId
        ? { kind: 'daily', localTime: trigger.localTime ?? '09:00', timeZoneId: trigger.timeZoneId }
        : { kind: 'daily', localTime: trigger.localTime ?? '09:00' }
    case 'event':
      return { kind: 'event', name: trigger.name ?? '' }
    default:
      return { kind: 'startup' }
  }
}

function parseRetry(value: unknown): PolicyRetry | null {
  if (!isObject(value)) return null
  const retry = value as Record<string, unknown>
  return {
    maximumRetries: typeof retry.maximumRetries === 'number' ? retry.maximumRetries : DEFAULT_RETRY.maximumRetries,
    initialSeconds: typeof retry.initialSeconds === 'number' ? retry.initialSeconds : DEFAULT_RETRY.initialSeconds,
    maximumSeconds: typeof retry.maximumSeconds === 'number' ? retry.maximumSeconds : DEFAULT_RETRY.maximumSeconds
  }
}

function isObject(value: unknown): value is JsonObject {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

// ---- detection rules ----

/**
 * Reads `parameters.detection` into the flat list the rule editor binds to. Content and signal rules
 * share one list because the agent's uniqueness check spans both, and two lists side by side make it
 * far too easy to add a name that already exists in the other one.
 */
export function readDetectionRules(parameters: JsonObject): DetectionRuleDraft[] {
  const detection = isObject(parameters.detection) ? parameters.detection : {}
  const content = Array.isArray(detection.contentRules) ? detection.contentRules : []
  const signal = Array.isArray(detection.signalRules) ? detection.signalRules : []

  const rules: DetectionRuleDraft[] = []
  for (const entry of content) {
    if (!isObject(entry)) continue
    rules.push({
      kind: 'content',
      name: typeof entry.name === 'string' ? entry.name : '',
      matchAny: readTerms(entry.matchAny),
      severity: typeof entry.severity === 'string' ? entry.severity : 'medium',
      caseSensitive: entry.caseSensitive === true,
      wholeWord: entry.wholeWord === true
    })
  }
  for (const entry of signal) {
    if (!isObject(entry)) continue
    rules.push({
      kind: 'signal',
      name: typeof entry.name === 'string' ? entry.name : '',
      matchAny: readTerms(entry.matchAny),
      caseSensitive: entry.caseSensitive === true
    })
  }
  return rules
}

export function readRedactMatchedText(parameters: JsonObject): boolean {
  const detection = isObject(parameters.detection) ? parameters.detection : {}
  return detection.redactMatchedText === true
}

/**
 * Writes the rule list back, preserving every other key of `parameters` and of `detection` itself.
 *
 * An empty list removes `detection` rather than writing an empty one: the agent treats a policy with
 * no detection block as "observe only", and an empty block is a block that has to be reasoned about.
 */
export function writeDetectionRules(
  parameters: JsonObject,
  rules: DetectionRuleDraft[],
  redactMatchedText: boolean
): JsonObject {
  const existing = isObject(parameters.detection) ? { ...(parameters.detection as JsonObject) } : {}
  const next = { ...parameters }

  if (rules.length === 0 && !redactMatchedText) {
    delete next.detection
    return next
  }

  const contentRules = rules
    .filter((rule): rule is ContentRuleDraft => rule.kind === 'content')
    .map((rule) => {
      const serialized: JsonObject = { name: rule.name, matchAny: [...rule.matchAny], severity: rule.severity }
      // Omitted when false, matching what the server writes: the agent defaults both to false, and a
      // template that spelled them out would differ byte-for-byte from the same rule added by hand.
      if (rule.caseSensitive) serialized.caseSensitive = true
      if (rule.wholeWord) serialized.wholeWord = true
      return serialized
    })

  const signalRules = rules
    .filter((rule): rule is SignalRuleDraft => rule.kind === 'signal')
    .map((rule) => {
      const serialized: JsonObject = { name: rule.name, matchAny: [...rule.matchAny] }
      if (rule.caseSensitive) serialized.caseSensitive = true
      return serialized
    })

  const detection: JsonObject = { ...existing }
  delete detection.contentRules
  delete detection.signalRules
  delete detection.redactMatchedText

  if (contentRules.length > 0) detection.contentRules = contentRules
  if (signalRules.length > 0) detection.signalRules = signalRules
  if (redactMatchedText) detection.redactMatchedText = true

  next.detection = detection
  return next
}

function readTerms(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((term): term is string => typeof term === 'string') : []
}

export function parseTerms(text: string): string[] {
  // Newline or comma: an operator pasting a column out of a spreadsheet gets one per line, and one
  // typing them out uses commas. Refusing either would be a rule with no purpose.
  return text
    .split(/[\n,]/)
    .map((term) => term.trim())
    .filter((term) => term.length > 0)
}

// ---- the account and its subscription plan ----

export interface AccountAlertDraft {
  ruleName: string
  severity: string
  onTiers: string[]
  requireContentMatch: boolean
}

export interface AccountDraft {
  /** Whether the assistant this policy watches has an account block at all. */
  available: boolean
  captureTier: boolean
  captureDisplayName: boolean
  /** The plan words this vendor uses, offered as the choices rather than typed out by hand. */
  tierVocabulary: string[]
  alert: AccountAlertDraft | null
}

/**
 * The name a tier match is reported under when a policy does not choose one.
 *
 * It matches the agent's own fallback, and it has to: the agent decides what the match is called and
 * the server decides which name a dictionary may not use, so two fallbacks would leave the reserved
 * name and the reported name different.
 */
export const DEFAULT_ACCOUNT_RULE_NAME = 'ai subscription tier'

const TIER = 'tier'
const DISPLAY_NAME = 'displayName'

export function readAccount(parameters: JsonObject): AccountDraft {
  const account = isObject(parameters.account) ? parameters.account : null
  if (!account)
    return { available: false, captureTier: false, captureDisplayName: false, tierVocabulary: [], alert: null }

  const capture = readTerms(account.capture)
  const alert = isObject(account.alert) ? account.alert : null

  return {
    available: true,
    captureTier: capture.includes(TIER),
    captureDisplayName: capture.includes(DISPLAY_NAME),
    tierVocabulary: readTerms(account.tierVocabulary),
    alert: alert
      ? {
          ruleName: typeof alert.ruleName === 'string' && alert.ruleName ? alert.ruleName : DEFAULT_ACCOUNT_RULE_NAME,
          severity: typeof alert.severity === 'string' ? alert.severity : 'high',
          onTiers: readTerms(alert.onTiers),
          requireContentMatch: alert.requireContentMatch === true
        }
      : null
  }
}

/**
 * Writes the account decisions back, keeping every key this form does not manage.
 *
 * The selectors, the vocabulary, the aliases and the region are the catalog's — they are how the
 * account row is found, and they are the part an operator has no reason to touch. Rebuilding the
 * block from the fields on screen would delete them and leave a policy that alerts on a plan it has
 * no way to read, which is the same defect the enforcement block used to suffer.
 *
 * Turning the capture off writes an empty list rather than removing the block, so the selectors are
 * still there when somebody turns it back on.
 */
export function writeAccount(parameters: JsonObject, account: AccountDraft): JsonObject {
  if (!account.available) return parameters

  const existing = isObject(parameters.account) ? { ...(parameters.account as JsonObject) } : {}
  const capture: string[] = []
  if (account.captureTier) capture.push(TIER)
  if (account.captureDisplayName) capture.push(DISPLAY_NAME)

  existing.capture = capture

  // An alert compares the plan, so one that survived the plan being switched off would be a policy
  // the server refuses to save with an error about a field this form is no longer showing.
  if (account.alert && account.captureTier) {
    const alert = isObject(existing.alert) ? { ...(existing.alert as JsonObject) } : {}
    alert.ruleName = account.alert.ruleName.trim() || DEFAULT_ACCOUNT_RULE_NAME
    alert.severity = account.alert.severity
    alert.onTiers = [...account.alert.onTiers]
    alert.requireContentMatch = account.alert.requireContentMatch
    existing.alert = alert
  } else {
    delete existing.alert
  }

  return { ...parameters, account: existing }
}

/**
 * The plans this policy can offer, which is the vendor's own list plus anything already chosen that
 * is not on it. The second half matters because changing the assistant is one click: a ChatGPT
 * policy carrying "plus" that is switched to Claude would otherwise lose it silently, and switching
 * back would not bring it back.
 */
export function offeredTiers(account: AccountDraft): string[] {
  const offered = account.tierVocabulary.map((tier) => tier.toLowerCase())
  for (const tier of account.alert?.onTiers ?? []) if (!offered.includes(tier)) offered.push(tier)
  return offered
}

// ---- what happens when a rule matches ----

/**
 * The enforcement decision, as the form offers it.
 *
 * `observe` is no block at all, which the agent reads as "observe only". `record` is the agent's
 * exclusive record action, which turns a match into a detection without touching anybody's prompt.
 * `act` is any combination of the interrupting actions and the notification.
 */
export type EnforcementMode = 'observe' | 'record' | 'act'

export interface EnforcementDraft {
  mode: EnforcementMode
  minimumSeverity: string
  blockSubmitKeys: boolean
  clearPrompt: boolean
  notifyUser: boolean
  /** Whether a match is also checked the moment somebody presses send, not only on the interval. */
  preSubmit: boolean
  blockDurationSeconds: number
  notificationTitle: string
  notificationMessage: string
}

/**
 * The agent's own defaults, and the values the bundled blocking template carries. Every spelling the
 * form writes is one the agent already parses (confirmed against client 1.2.2): it throws on an
 * unknown enum value, so a value invented in this console would fail the whole policy on every agent.
 */
export const ENFORCEMENT_DEFAULTS = {
  minimumSeverity: 'high',
  blockDurationSeconds: 120,
  maximumBlockDurationSeconds: 3600,
  maximumNotificationLength: 256,
  submitKeys: ['Enter', 'Ctrl+Enter'],
  notificationTitle: 'Prompt blocked',
  notificationMessage: 'Coastline blocked this prompt in {app} because it matched {rules}.'
} as const

/** Actions this form manages. Anything else in the list (showOverlay today) is carried untouched. */
const FORM_ACTIONS = ['record', 'blockSubmitKeys', 'clearPrompt', 'notifyUser']
const MAXIMUM_ACTIONS = 5

export function readEnforcement(parameters: JsonObject): EnforcementDraft {
  const enforcement = isObject(parameters.enforcement) ? parameters.enforcement : null
  const actions = enforcement ? readTerms(enforcement.actions) : []
  const modes = enforcement ? readTerms(enforcement.detectionModes) : []
  const mode: EnforcementMode =
    !enforcement || actions.length === 0 ? 'observe' : actions.includes('record') ? 'record' : 'act'

  return {
    mode,
    minimumSeverity:
      enforcement && typeof enforcement.minimumSeverity === 'string'
        ? enforcement.minimumSeverity
        : ENFORCEMENT_DEFAULTS.minimumSeverity,
    blockSubmitKeys: actions.includes('blockSubmitKeys'),
    clearPrompt: actions.includes('clearPrompt'),
    notifyUser: actions.includes('notifyUser'),
    preSubmit: modes.includes('preSubmit'),
    blockDurationSeconds:
      enforcement && typeof enforcement.blockDurationSeconds === 'number'
        ? enforcement.blockDurationSeconds
        : ENFORCEMENT_DEFAULTS.blockDurationSeconds,
    notificationTitle:
      enforcement && typeof enforcement.notificationTitle === 'string'
        ? enforcement.notificationTitle
        : ENFORCEMENT_DEFAULTS.notificationTitle,
    notificationMessage:
      enforcement && typeof enforcement.notificationMessage === 'string'
        ? enforcement.notificationMessage
        : ENFORCEMENT_DEFAULTS.notificationMessage
  }
}

/**
 * Writes the decision back, keeping every key of the block this form does not manage.
 *
 * Three rules from the agent shape this, and each is a refusal of the whole policy when broken:
 * `record` must be the only action, the action list may not be empty, and no entry may repeat. So
 * "observe" removes the block rather than writing an empty list, and "record" replaces the list.
 * Keys the form does not show - `appliesTo`, `overlayMessage`, `preSubmitBudgetMilliseconds`, an
 * existing `showOverlay` - survive, for the same reason `parameters` is edited in place at all.
 */
export function writeEnforcement(parameters: JsonObject, draft: EnforcementDraft): JsonObject {
  const next = { ...parameters }
  if (draft.mode === 'observe') {
    delete next.enforcement
    return next
  }

  const existing = isObject(parameters.enforcement) ? { ...(parameters.enforcement as JsonObject) } : {}
  const carried = readTerms(existing.actions).filter((action) => !FORM_ACTIONS.includes(action))
  existing.minimumSeverity = draft.minimumSeverity

  if (draft.mode === 'record') {
    existing.actions = ['record']
    delete existing.blockDurationSeconds
    delete existing.submitKeys
    delete existing.notificationTitle
    delete existing.notificationMessage
    const modes = readTerms(existing.detectionModes).filter((mode) => mode !== 'preSubmit')
    if (modes.length === 0) delete existing.detectionModes
    else existing.detectionModes = modes
    return { ...next, enforcement: existing }
  }

  const actions: string[] = []
  if (draft.blockSubmitKeys) actions.push('blockSubmitKeys')
  if (draft.clearPrompt) actions.push('clearPrompt')
  if (draft.notifyUser) actions.push('notifyUser')
  for (const action of carried) if (!actions.includes(action)) actions.push(action)
  // An act with nothing ticked is not an act. An empty list is refused by the agent, so the closest
  // honest document is a notification, which interrupts nobody. The form never gets here - it will
  // not untick the last action - so this guards a caller that builds a draft by hand.
  if (actions.length === 0) actions.push('notifyUser')
  // The agent refuses more than five.
  existing.actions = actions.slice(0, MAXIMUM_ACTIONS)

  const modes = readTerms(existing.detectionModes).filter((mode) => mode !== 'preSubmit')
  if (!modes.includes('interval')) modes.unshift('interval')
  if (draft.preSubmit) modes.push('preSubmit')
  existing.detectionModes = modes

  if (actions.includes('blockSubmitKeys')) {
    existing.blockDurationSeconds = Math.min(
      ENFORCEMENT_DEFAULTS.maximumBlockDurationSeconds,
      Math.max(1, Math.round(draft.blockDurationSeconds))
    )
    if (!Array.isArray(existing.submitKeys)) existing.submitKeys = [...ENFORCEMENT_DEFAULTS.submitKeys]
  } else {
    delete existing.blockDurationSeconds
    delete existing.submitKeys
  }

  if (actions.includes('notifyUser')) {
    existing.notificationTitle = draft.notificationTitle.slice(0, ENFORCEMENT_DEFAULTS.maximumNotificationLength)
    existing.notificationMessage = draft.notificationMessage.slice(0, ENFORCEMENT_DEFAULTS.maximumNotificationLength)
  } else {
    delete existing.notificationTitle
    delete existing.notificationMessage
  }

  return { ...next, enforcement: existing }
}

/**
 * Whether the watched assistant runs in a terminal. A terminal cannot be interrupted safely: clearing
 * a prompt cannot clear a shell line, and holding the send keys freezes the whole terminal window, so
 * the form offers record-only there.
 */
export function watchesTerminal(parameters: JsonObject): boolean {
  return parameters.surface === 'terminal'
}

/**
 * What a prompt watcher is about to do that is almost certainly not what was meant.
 *
 * These are not validation: the server's validator decides what the agent accepts, and this never
 * stops a save. They are the ways a document the agent accepts can still do nothing, which the page
 * used to call "valid" without a word, and each one names the fix on this page.
 */
export function watcherAdvisories(draft: PolicyDraft, boundDictionaries: number): string[] {
  if (draft.capability !== 'agent-prompt-observe') return []
  const advisories: string[] = []
  const rules = readDetectionRules(draft.parameters)
  const account = readAccount(draft.parameters)
  const enforcement = readEnforcement(draft.parameters)
  const nothingToMatch = rules.length === 0 && boundDictionaries === 0

  if (nothingToMatch && !account.captureTier && !account.captureDisplayName)
    advisories.push(
      'This watches for nothing: no dictionary is bound, no rule is written into it, and no account is read. Bind a dictionary under "What it looks for".'
    )
  else if (nothingToMatch && enforcement.mode !== 'observe' && !account.alert)
    advisories.push(
      'Nothing can match, so what is chosen under "When a rule matches" never happens. Bind a dictionary under "What it looks for".'
    )

  if (draft.trigger.kind === 'startup')
    advisories.push(
      'It runs once, when Coastline starts, so it sees whatever is in the prompt box at sign-in and nothing after. Run it on an interval instead.'
    )

  if (!draft.publish && enforcement.mode === 'observe' && !account.alert)
    advisories.push(
      'Nothing it finds reaches this console: a match neither records a detection nor blocks, and its results are not sent. Choose what happens under "When a rule matches", or send the results.'
    )

  return advisories
}

// ---- the sentence in the header ----

const UNITS: [number, string][] = [
  [86400, 'day'],
  [3600, 'hour'],
  [60, 'minute']
]

export function describeSeconds(seconds: number): string {
  for (const [size, unit] of UNITS) {
    if (seconds >= size && seconds % size === 0) {
      const count = seconds / size
      return `${count} ${unit}${count === 1 ? '' : 's'}`
    }
  }
  return `${seconds} second${seconds === 1 ? '' : 's'}`
}

export function describeTrigger(trigger: PolicyTrigger): string {
  switch (trigger.kind) {
    case 'interval':
      return `Every ${describeSeconds(trigger.seconds ?? 300)}`
    case 'daily':
      return `Every day at ${trigger.localTime ?? '09:00'} (${trigger.timeZoneId ?? 'device local time'})`
    case 'event':
      return `When ${trigger.name || 'an event'} occurs`
    default:
      return 'When Coastline starts'
  }
}

/**
 * The policy in one sentence.
 *
 * It is the single most useful thing on the page. An operator who inherited forty policies reads this
 * to find out what one does; reading the JSON to answer the same question takes a minute and is
 * where mistakes come from.
 */
export function describeDraft(draft: PolicyDraft, capabilityName: (capability: string) => string): string {
  const rules = readDetectionRules(draft.parameters)
  const detection =
    rules.length === 0
      ? ''
      : ` and check the text against ${rules.length} detection rule${rules.length === 1 ? '' : 's'}`

  const account = describeAccount(readAccount(draft.parameters))

  const condition =
    draft.trigger.kind === 'event' && draft.condition.trim() ? `, but only when ${draft.condition.trim()}` : ''

  const outcome = !draft.publish
    ? 'Results are recorded on the device only.'
    : draft.publishCondition.trim()
      ? `Results are recorded on the device, and sent to the collector when ${draft.publishCondition.trim()}.`
      : 'Every result is recorded on the device and sent to the collector.'

  const retry = draft.retry ? ` Failures retry up to ${draft.retry.maximumRetries} time(s).` : ''

  return `${describeTrigger(draft.trigger)}${condition}, ${lowerFirst(capabilityName(draft.capability))}${detection}.${account}${describeEnforcement(readEnforcement(draft.parameters))} ${outcome}${retry}`
}

function describeAccount(account: AccountDraft): string {
  if (!account.available || (!account.captureTier && !account.captureDisplayName)) return ''

  const read = account.captureTier && account.captureDisplayName
    ? 'the signed-in account and its plan'
    : account.captureTier
      ? 'the subscription plan'
      : 'the signed-in account'

  if (!account.alert || account.alert.onTiers.length === 0) return ` Also record ${read}.`

  const tiers = account.alert.onTiers.join(', ')
  return account.alert.requireContentMatch
    ? ` Also record ${read}, and alert when the plan is ${tiers} and a rule matched.`
    : ` Also record ${read}, and alert when the plan is ${tiers}.`
}

/**
 * Lowercases only the first letter, so "Watch AI assistant prompts" reads "watch AI assistant
 * prompts" mid-sentence rather than "watch ai assistant prompts".
 */
function lowerFirst(text: string): string {
  return text.length === 0 ? text : text.charAt(0).toLowerCase() + text.slice(1)
}

function describeEnforcement(enforcement: EnforcementDraft): string {
  if (enforcement.mode === 'observe') return ''
  const at = `a ${enforcement.minimumSeverity} match or worse`
  if (enforcement.mode === 'record') return ` Record ${at} as a detection.`

  const verbs: string[] = []
  if (enforcement.blockSubmitKeys) verbs.push('hold the send')
  if (enforcement.clearPrompt) verbs.push('clear the prompt')
  if (enforcement.notifyUser || verbs.length === 0) verbs.push('tell the person')
  const list = verbs.length === 1 ? verbs[0] : `${verbs.slice(0, -1).join(', ')} and ${verbs[verbs.length - 1]}`
  return ` On ${at}, ${list}.`
}
